// [INÍCIO] Classificação da Análise: eixos estáveis e retrato da rodada (ADR 0083)
import {
  ROTULO_DA_ETAPA,
  ROTULO_DA_URGENCIA,
  ROTULO_DO_MOTIVO,
  ROTULO_DO_PAPEL,
  type PerfilDoLead,
} from './perfil-lead'
import type { EixoIaDto, LinhaEixoIaDto, OrigemEixoIa } from './contratos/ia'

/**
 * A Análise tem dois andares. O estável sai do perfil já gravado em cada
 * conversa (papel, etapa, motivo, urgência, matrícula IA) e não muda quando
 * alguém clica de novo. O dinâmico nasce na rodada: a IA inventa eixos, aponta
 * cada resumo, e o código conta. A próxima análise substitui esse retrato.
 */

export const TETO_EIXOS_DA_ANALISE = 4
export const TETO_LINHAS_DA_ANALISE = 8

export const EIXOS_ESTAVEIS = ['papel', 'etapa', 'motivo', 'urgencia', 'matricula'] as const
export type EixoEstavel = (typeof EIXOS_ESTAVEIS)[number]

export const ROTULO_EIXO_ESTAVEL: Record<EixoEstavel, string> = {
  papel: 'Quem escreve',
  etapa: 'Etapa',
  motivo: 'Motivo',
  urgencia: 'Urgência',
  matricula: 'Confirmação',
}

export const ROTULO_DA_MATRICULA_IA: Record<'sim' | 'nao' | 'indefinido', string> = {
  sim: 'Confirmou',
  nao: 'Desistiu',
  indefinido: 'Em andamento',
}

const ROTULOS_ESTAVEIS: Record<EixoEstavel, Record<string, string>> = {
  papel: ROTULO_DO_PAPEL,
  etapa: ROTULO_DA_ETAPA,
  motivo: ROTULO_DO_MOTIVO,
  urgencia: ROTULO_DA_URGENCIA,
  matricula: ROTULO_DA_MATRICULA_IA,
}

const CHAVES_RESERVADAS = new Set<string>(EIXOS_ESTAVEIS)

export interface ChamadoParaEixo {
  matriculaIa: string | null
  perfil: PerfilDoLead | null
}

function ehConfirmada(matricula: string | null): boolean {
  return matricula === 'sim'
}

function linha(valor: string, rotulo: string, conversas: number, ganhas: number): LinhaEixoIaDto {
  return {
    valor,
    rotulo,
    conversas,
    ganhas,
    taxa: conversas > 0 ? ganhas / conversas : null,
  }
}

function linhasDeContagem(
  contagens: Map<string, { conversas: number; ganhas: number }>,
  rotuloDe: (valor: string) => string,
): LinhaEixoIaDto[] {
  const linhas: LinhaEixoIaDto[] = []
  for (const [valor, c] of contagens) {
    linhas.push(linha(valor, rotuloDe(valor), c.conversas, c.ganhas))
  }
  linhas.sort((a, b) => b.conversas - a.conversas)
  return linhas
}

function somar(
  mapa: Map<string, { conversas: number; ganhas: number }>,
  chave: string,
  ganha: boolean,
) {
  const atual = mapa.get(chave) ?? { conversas: 0, ganhas: 0 }
  atual.conversas += 1
  if (ganha) atual.ganhas += 1
  mapa.set(chave, atual)
}

/** Conta as conversas resumidas nos cinco eixos que não mudam com a rodada. */
export function montarEixosDoPerfil(chamados: ChamadoParaEixo[]): {
  eixos: EixoIaDto[]
  conversas: number
  comPerfil: number
  confirmadas: number
} {
  const contagens = new Map<string, { conversas: number; ganhas: number }>()
  let comPerfil = 0
  let confirmadas = 0

  for (const c of chamados) {
    const ganha = ehConfirmada(c.matriculaIa)
    if (ganha) confirmadas += 1
    if (c.perfil) comPerfil += 1
    for (const eixo of EIXOS_ESTAVEIS) {
      const valor =
        eixo === 'matricula'
          ? c.matriculaIa === 'sim' || c.matriculaIa === 'nao' || c.matriculaIa === 'indefinido'
            ? c.matriculaIa
            : 'sem_dado'
          : c.perfil?.[eixo] ?? 'sem_dado'
      somar(contagens, `${eixo}:${valor}`, ganha)
    }
  }

  const eixos: EixoIaDto[] = EIXOS_ESTAVEIS.map((eixo) => {
    const doEixo = new Map<string, { conversas: number; ganhas: number }>()
    for (const [chave, c] of contagens) {
      if (!chave.startsWith(`${eixo}:`)) continue
      doEixo.set(chave.slice(eixo.length + 1), c)
    }
    return {
      eixo,
      titulo: ROTULO_EIXO_ESTAVEL[eixo],
      origem: 'perfil' as OrigemEixoIa,
      linhas: linhasDeContagem(doEixo, (valor) =>
        valor === 'sem_dado' ? 'Não lido' : (ROTULOS_ESTAVEIS[eixo][valor] ?? valor),
      ),
    }
  })

  return { eixos, conversas: chamados.length, comPerfil, confirmadas }
}

function slug(bruto: unknown): string | null {
  if (typeof bruto !== 'string') return null
  const limpo = bruto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32)
  if (!/^[a-z][a-z0-9_]{1,31}$/.test(limpo)) return null
  if (CHAVES_RESERVADAS.has(limpo)) return null
  return limpo
}

function rotuloCurto(bruto: unknown, fallback: string): string {
  const texto = typeof bruto === 'string' ? bruto.trim().replace(/\s+/g, ' ') : ''
  const cortado = (texto || fallback).slice(0, 60)
  return cortado || fallback
}

/**
 * Lê a resposta da análise, classifica cada resumo nos eixos inventados e
 * devolve a contagem. Número inventado pela IA não entra: quem soma é aqui.
 */
export function contarClassificacaoDaAnalise(
  lote: ChamadoParaEixo[],
  bruto: unknown,
): EixoIaDto[] {
  if (!bruto || typeof bruto !== 'object' || Array.isArray(bruto)) return []
  const corpo = bruto as Record<string, unknown>
  if (!Array.isArray(corpo.eixos) || !Array.isArray(corpo.itens)) return []

  const eixosDeclarados: { chave: string; titulo: string; valores: Map<string, string> }[] = []
  const chavesVistas = new Set<string>()
  for (const item of corpo.eixos.slice(0, TETO_EIXOS_DA_ANALISE)) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue
    const e = item as Record<string, unknown>
    const chave = slug(e.chave ?? e.eixo)
    if (!chave || chavesVistas.has(chave)) continue
    chavesVistas.add(chave)
    const valores = new Map<string, string>()
    const lista = Array.isArray(e.valores) ? e.valores : Array.isArray(e.linhas) ? e.linhas : []
    for (const v of lista.slice(0, TETO_LINHAS_DA_ANALISE)) {
      if (!v || typeof v !== 'object' || Array.isArray(v)) continue
      const linhaBruta = v as Record<string, unknown>
      const valor = slug(linhaBruta.valor ?? linhaBruta.chave)
      if (!valor || valores.has(valor)) continue
      valores.set(valor, rotuloCurto(linhaBruta.rotulo ?? linhaBruta.titulo, valor))
    }
    if (valores.size === 0) continue
    eixosDeclarados.push({
      chave,
      titulo: rotuloCurto(e.titulo ?? e.rotulo, chave),
      valores,
    })
  }
  if (eixosDeclarados.length === 0) return []

  const porIndice = new Map<number, Record<string, unknown>>()
  for (const item of corpo.itens) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue
    const linhaItem = item as Record<string, unknown>
    const i = typeof linhaItem.i === 'number' ? linhaItem.i : Number(linhaItem.i)
    if (!Number.isInteger(i) || i < 1 || i > lote.length) continue
    const extra =
      linhaItem.chaves && typeof linhaItem.chaves === 'object' && !Array.isArray(linhaItem.chaves)
        ? (linhaItem.chaves as Record<string, unknown>)
        : {}
    porIndice.set(i, { ...extra, ...linhaItem })
  }

  const saida: EixoIaDto[] = []
  for (const eixo of eixosDeclarados) {
    const contagens = new Map<string, { conversas: number; ganhas: number }>()
    for (let i = 1; i <= lote.length; i += 1) {
      const marcado = porIndice.get(i)
      if (!marcado) continue
      const brutoValor = marcado[eixo.chave]
      const valor = slug(brutoValor)
      if (!valor || !eixo.valores.has(valor)) continue
      somar(contagens, valor, ehConfirmada(lote[i - 1]?.matriculaIa ?? null))
    }
    const linhas = linhasDeContagem(contagens, (valor) => eixo.valores.get(valor) ?? valor)
    if (linhas.length === 0) continue
    saida.push({
      eixo: eixo.chave,
      titulo: eixo.titulo,
      origem: 'analise',
      linhas,
    })
  }
  return saida
}

/** Relê o JSON gravado em analises_ia.eixos. Lixo ou formato antigo vira lista vazia. */
export function lerEixosGravados(bruto: unknown): EixoIaDto[] {
  if (!Array.isArray(bruto)) return []
  const saida: EixoIaDto[] = []
  for (const item of bruto.slice(0, TETO_EIXOS_DA_ANALISE)) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue
    const e = item as Record<string, unknown>
    const eixo = slug(e.eixo)
    if (!eixo) continue
    if (e.origem !== 'analise') continue
    if (!Array.isArray(e.linhas)) continue
    const linhas: LinhaEixoIaDto[] = []
    for (const l of e.linhas.slice(0, TETO_LINHAS_DA_ANALISE)) {
      if (!l || typeof l !== 'object' || Array.isArray(l)) continue
      const linhaBruta = l as Record<string, unknown>
      const valor = slug(linhaBruta.valor)
      const conversas = Number(linhaBruta.conversas)
      const ganhas = Number(linhaBruta.ganhas)
      if (!valor || !Number.isFinite(conversas) || conversas < 0) continue
      if (!Number.isFinite(ganhas) || ganhas < 0) continue
      const taxaBruta = linhaBruta.taxa
      const taxa =
        typeof taxaBruta === 'number' && Number.isFinite(taxaBruta)
          ? taxaBruta
          : conversas > 0
            ? ganhas / conversas
            : null
      linhas.push({
        valor,
        rotulo: rotuloCurto(linhaBruta.rotulo, valor),
        conversas: Math.floor(conversas),
        ganhas: Math.floor(ganhas),
        taxa,
      })
    }
    if (linhas.length === 0) continue
    saida.push({
      eixo,
      titulo: rotuloCurto(e.titulo, eixo),
      origem: 'analise',
      linhas,
    })
  }
  return saida
}
// [FIM] Classificação da Análise (ADR 0083)
